import { createContext, useContext, useState, useCallback } from "react";

// Spanish translations keyed by the English string. Missing keys fall back to English.
const ES = {
  // Nav
  "Home": "Inicio",
  "About Us": "Nosotros",
  "Services": "Servicios",
  "Who We Serve": "A quién servimos",
  "Service Areas": "Áreas de servicio",
  "Contact": "Contacto",
  "Join Our Team": "Únete al equipo",
  "Schedule a Tour": "Agenda una visita",
  "Request Info": "Solicitar info",
  "Request Information": "Solicitar información",
  "Call Us": "Llámanos",
  "Our Services": "Nuestros servicios",
  // Hero
  "Compassionate developmental care for growing children":
    "Cuidado del desarrollo con compasión para niños en crecimiento",
  "Amenity Health Services and Amenity ABA Center partners with families to support children through speech, behavioral, and developmental care — delivered with warmth, patience, and respect.":
    "Amenity Health Services y Amenity ABA Center colaboran con las familias para apoyar a los niños con terapia del habla, del comportamiento y del desarrollo, brindada con calidez, paciencia y respeto.",
  // Insurances
  "Insurances we accept": "Seguros que aceptamos",
  "We proudly partner with a range of insurance providers to help make care accessible for your family.":
    "Trabajamos con diversas aseguradoras para ayudar a que la atención sea accesible para tu familia.",
  // Footer
  "talk.": "Hablemos.",
  "Explore": "Explorar",
  "Follow": "Síguenos",
  "Privacy Policy": "Política de privacidad",
  "Accessibility Statement": "Declaración de accesibilidad",
  "All rights reserved.": "Todos los derechos reservados.",
  // Contact form
  "Full name": "Nombre completo",
  "Phone": "Teléfono",
  "Email": "Correo electrónico",
  "Select all services that your family is interested in":
    "Selecciona todos los servicios que le interesan a tu familia",
  "(ABA only) Has your child received an official autism diagnosis?":
    "(Solo ABA) ¿Tu hijo/a ha recibido un diagnóstico oficial de autismo?",
  "Yes": "Sí",
  "No": "No",
  "Not sure": "No estoy seguro/a",
  "Preferred contact method": "Método de contacto preferido",
  "email": "correo",
  "phone": "teléfono",
  "either": "cualquiera",
  "How can we help?": "¿Cómo podemos ayudar?",
  "Attach files or pictures": "Adjunta archivos o fotos",
  "Attach Referrals": "Adjuntar referencia",
  "Attach Referral": "Adjuntar referencia",
  "Choose files to upload": "Elige archivos para subir",
  "Uploading…": "Subiendo…",
  "Sending…": "Enviando…",
  "Request your tour": "Solicita tu visita",
  "Request Tour": "Solicitar visita",

  // Page headers
  "About us": "Nosotros",
  "A caring team,": "Un equipo que cuida,",
  "here for your family": "aquí para tu familia",
  "Amenity Health Services exists to help children grow, communicate, and thrive — supported by a team that treats every family with warmth and respect.":
    "Amenity Health Services existe para ayudar a los niños a crecer, comunicarse y prosperar, con un equipo que trata a cada familia con calidez y respeto.",
  "Accessibility": "Accesibilidad",
  "Statement": "Declaración",
  "We are committed to making this website welcoming and usable for everyone, including people with disabilities.":
    "Nos comprometemos a que este sitio web sea acogedor y utilizable para todos, incluidas las personas con discapacidad.",
  "Let's start": "Comencemos",
  "a conversation": "una conversación",
  "Reach out with any questions — no pressure, no medical details needed yet. We'll get back to you and walk you through the next steps.":
    "Escríbenos con cualquier pregunta, sin compromiso y sin necesidad de datos médicos aún. Te responderemos y te guiaremos en los siguientes pasos.",
  "Careers": "Empleo",
  "Join our": "Únete a",
  "team": "nuestro equipo",
  "We're always looking for compassionate, dedicated people who share our passion for helping children thrive. If that sounds like you, we'd love to hear from you.":
    "Siempre buscamos personas dedicadas y compasivas que compartan nuestra pasión por ayudar a los niños a prosperar. Si eso te describe, nos encantaría conocerte.",
  "Legal": "Legal",
  "Visit us": "Visítanos",
  "Schedule": "Agenda",
  "a tour": "una visita",
  "Come see our space and meet our team. Choose a location and a time that works for your family — we'll take care of the rest.":
    "Ven a conocer nuestro espacio y a nuestro equipo. Elige una ubicación y un horario que funcione para tu familia; nosotros nos encargamos del resto.",
  "Service areas": "Áreas de servicio",
  "Serving families": "Servimos a familias",
  "across Houston": "en Houston",
  "We provide care throughout the communities listed below. If you don't see your area, please reach out — we may still be able to help or connect you with resources.":
    "Brindamos atención en las comunidades listadas abajo. Si no ves tu área, contáctanos: es posible que aún podamos ayudarte o conectarte con recursos.",
  "Support that meets": "Apoyo que acompaña",
  "children where they are": "a los niños donde están",
  "Each of our services is designed around the child — playful, patient, and shaped by your family's goals.":
    "Cada uno de nuestros servicios se diseña en torno al niño: lúdico, paciente y guiado por las metas de tu familia.",
  "Who we serve": "A quién servimos",
  "Support for everyone": "Apoyo para todos",
  "in a child's circle": "en el círculo del niño",
  "Great care extends beyond the child. We partner with families, caregivers, and referral partners to create a circle of support around every child we serve.":
    "El buen cuidado va más allá del niño. Colaboramos con familias, cuidadores y socios de referencia para crear un círculo de apoyo alrededor de cada niño.",

  // Home + growth journey
  "Who we help": "A quién ayudamos",
  "Our services": "Nuestros servicios",
  "Ways we can support you": "Cómo podemos apoyarte",
  "Why families choose us": "Por qué las familias nos eligen",
  "Trust, built through everyday care": "Confianza, construida con el cuidado diario",
  "Kind words": "Palabras amables",
  "Stories from families": "Historias de familias",
  "Ready to take the first step?": "¿Listo para dar el primer paso?",
  "Every child's journey": "El camino de cada niño",
  "Growth, one gentle step at a time": "Crecer, un paso suave a la vez",
  "Just like a caterpillar becomes a butterfly, every child grows at their own pace — and we're here to nurture each stage of the way.":
    "Así como una oruga se convierte en mariposa, cada niño crece a su propio ritmo, y estamos aquí para acompañar cada etapa del camino.",
  "Caterpillar": "Oruga",
  "Chrysalis": "Crisálida",
  "Emerging": "Emergiendo",
  "Butterfly": "Mariposa",
  "Where every journey begins": "Donde empieza todo camino",
  "A safe space to grow": "Un espacio seguro para crecer",
  "Finding new strength": "Descubriendo nueva fuerza",
  "Ready to take flight": "Lista para volar",
  "Get in touch": "Ponte en contacto",
  "Business hours": "Horario de atención",
  "Learn more": "Ver más",
  "View all services": "Ver todos los servicios",

  // Home — body copy
  "Care built around your child, and your family.": "Atención pensada para tu hijo/a y para tu familia.",
  "We support children who are working on communication, behavior, and everyday developmental skills — and we support the families walking alongside them. From your very first question to ongoing sessions, our team keeps things warm, clear, and centered on what matters most to you.":
    "Apoyamos a los niños que trabajan en habilidades de comunicación, comportamiento y desarrollo diario, y también apoyamos a las familias que los acompañan. Desde tu primera pregunta hasta las sesiones continuas, nuestro equipo lo mantiene todo cálido, claro y centrado en lo que más te importa.",
  "Explore our services": "Explora nuestros servicios",
  "Proudly serving families across Houston": "Con orgullo servimos a familias en todo Houston",
  "We work with families throughout the areas below. Not sure if you're in our service area? Reach out — we're always happy to help point you in the right direction.":
    "Trabajamos con familias en todas las áreas listadas abajo. ¿No estás seguro/a si estás en nuestra área de servicio? Contáctanos: siempre estamos felices de orientarte en la dirección correcta.",
  "See service areas": "Ver áreas de servicio",
  "Tell us a little about your family, and we'll help you understand the next steps — at your pace.":
    "Cuéntanos un poco sobre tu familia y te ayudaremos a entender los siguientes pasos, a tu propio ritmo.",

  // Marquee words
  "Compassionate": "Compasivos",
  "Patient": "Pacientes",
  "Family-first": "La familia primero",
  "Respectful": "Respetuosos",
  "Playful": "Lúdicos",
  "Encouraging": "Alentadores",
  "Trusted": "De confianza",

  // Trust points
  "Family-centered": "Centrados en la familia",
  "We listen first. Families are partners in every step of a child's care journey.":
    "Escuchamos primero. Las familias son socias en cada paso del camino de cuidado de un niño.",
  "Warm & patient": "Cálidos y pacientes",
  "Sessions are gentle, playful, and paced around each child's comfort.":
    "Las sesiones son suaves, lúdicas y a un ritmo pensado para la comodidad de cada niño.",
  "Clear communication": "Comunicación clara",
  "You'll always know what's happening and what comes next — no jargon.":
    "Siempre sabrás qué está pasando y qué sigue, sin tecnicismos.",
  "Respectful care": "Cuidado respetuoso",
  "Every child and family is met with dignity, kindness, and understanding.":
    "Cada niño y familia es recibido con dignidad, amabilidad y comprensión.",

  // Hours
  "Monday – Friday": "Lunes – Viernes",
  "Saturday – Sunday": "Sábado – Domingo",
  "Closed": "Cerrado",

  // Services page
  "Service": "Servicio",
  "Who it's for": "Para quién es",
  "What it includes": "Qué incluye",
  "How to get started": "Cómo empezar",
  "Getting started": "Cómo empezar",
  "How someone can begin services": "Cómo se pueden iniciar los servicios",
  "Reach out": "Contáctanos",
  "Call us or submit a request through the contact form — whatever's easiest for you.":
    "Llámanos o envía una solicitud a través del formulario de contacto, lo que te resulte más fácil.",
  "Talk it through": "Conversemos",
  "We'll listen to your questions and help you understand possible next steps.":
    "Escucharemos tus preguntas y te ayudaremos a entender los posibles siguientes pasos.",
  "Begin together": "Comencemos juntos",
  "When you're ready, we'll guide you through getting started, at your family's pace.":
    "Cuando estés listo/a, te guiaremos para comenzar, al ritmo de tu familia.",
  "Call": "Llama al",

  // Service names
  "ABA Therapy": "Terapia ABA",
  "Speech Therapy": "Terapia del habla",
  "Physical Therapy": "Terapia física",
  "Occupational Therapy": "Terapia ocupacional",

  // Service descriptions (short)
  "At its heart, ABA is a personalized approach to learning that uses data to understand and support your child's unique needs. By focusing on essential social, communicative, and self-regulation skills, we provide your family with a roadmap for growth. We aren't just teaching skills; we're building the foundation for your child's next big milestone.":
    "En esencia, ABA es un enfoque personalizado del aprendizaje que utiliza datos para comprender y apoyar las necesidades únicas de tu hijo/a. Al enfocarnos en habilidades esenciales sociales, comunicativas y de autorregulación, le damos a tu familia una guía para el crecimiento. No solo enseñamos habilidades; construimos la base para el próximo gran logro de tu hijo/a.",
  "Speech therapy is the assessment and treatment that addresses various speech and language disorders. Speech therapy is used to help improve deficits by empowering both children and their parents with strategies to help them be successful in interacting with the world around them.":
    "La terapia del habla es la evaluación y el tratamiento que aborda diversos trastornos del habla y del lenguaje. Se utiliza para ayudar a mejorar las dificultades, brindando tanto a los niños como a sus padres estrategias para tener éxito al interactuar con el mundo que los rodea.",
  "As movement experts, Physical Therapy at Amenity House focuses on promoting development, enhancing learning and independence, reducing pain, improving strength and function, and easing daily challenges for caregivers for a variety of conditions, illnesses, and injuries in persons from birth to 21 years of age. We use a wide range of methods from a play-based approach to more traditional interventions following the most current evidence in the field to address each child's and family's needs!":
    "Como expertos en el movimiento, la Terapia Física en Amenity House se enfoca en promover el desarrollo, mejorar el aprendizaje y la independencia, reducir el dolor, aumentar la fuerza y la función, y aliviar los retos diarios de los cuidadores, para una variedad de condiciones, enfermedades y lesiones en personas desde el nacimiento hasta los 21 años. ¡Usamos una amplia gama de métodos, desde un enfoque lúdico hasta intervenciones más tradicionales, siguiendo la evidencia más actual para atender las necesidades de cada niño y familia!",
  "At Amenity House, the goal of Occupational therapy is to develop and nurture your child's everyday life skills with an inclusive and patient-centered approach designed with our patients, families, and caregivers in mind to enhance the uniqueness in every child. We do this by teaching and educating our patients on daily life skills such as handwriting, dressing, toilet/potty training, social interaction/engagement, and how to self-regulate, using sensory approaches that require therapeutic and creative interventions for your child's increased independence.":
    "En Amenity House, el objetivo de la Terapia Ocupacional es desarrollar y fortalecer las habilidades de la vida diaria de tu hijo/a con un enfoque inclusivo y centrado en el paciente, diseñado pensando en nuestros pacientes, familias y cuidadores para realzar lo único de cada niño. Lo hacemos enseñando habilidades de la vida diaria como la escritura, el vestirse, el entrenamiento para ir al baño, la interacción social y la autorregulación, usando enfoques sensoriales que requieren intervenciones terapéuticas y creativas para aumentar la independencia de tu hijo/a.",

  // Service "for"
  "Children working on social, communication, and self-regulation skills.":
    "Niños que trabajan en habilidades sociales, de comunicación y de autorregulación.",
  "Children with speech and language differences — and their parents.":
    "Niños con diferencias del habla y del lenguaje, y sus padres.",
  "Children from birth to 21 years across a range of conditions, illnesses, and injuries.":
    "Niños desde el nacimiento hasta los 21 años con diversas condiciones, enfermedades y lesiones.",
  "Children building everyday life skills with family-centered support.":
    "Niños que desarrollan habilidades de la vida diaria con apoyo centrado en la familia.",

  // Service includes
  "Personalized, data-informed learning plans": "Planes de aprendizaje personalizados y basados en datos",
  "Focus on social, communication & self-regulation skills": "Enfoque en habilidades sociales, de comunicación y de autorregulación",
  "A clear roadmap toward each next milestone": "Una guía clara hacia cada próximo logro",
  "Assessment of speech & language needs": "Evaluación de las necesidades del habla y del lenguaje",
  "Treatment to improve communication deficits": "Tratamiento para mejorar las dificultades de comunicación",
  "Strategies that empower both children and parents": "Estrategias que fortalecen tanto a los niños como a los padres",
  "Play-based and evidence-based interventions": "Intervenciones lúdicas y basadas en evidencia",
  "Focus on strength, function & independence": "Enfoque en la fuerza, la función y la independencia",
  "Support that eases daily challenges for caregivers": "Apoyo que alivia los retos diarios de los cuidadores",
  "Handwriting, dressing, and toileting skills": "Habilidades de escritura, vestido y uso del baño",
  "Social interaction, engagement & self-regulation": "Interacción social, participación y autorregulación",
  "Sensory-based, creative therapeutic activities": "Actividades terapéuticas creativas y basadas en lo sensorial",

  // Service start
  "Request information and our team will walk you through next steps.":
    "Solicita información y nuestro equipo te guiará en los siguientes pasos.",
  "Reach out and we'll help you understand how to begin.":
    "Contáctanos y te ayudaremos a entender cómo comenzar.",
  "Contact us to learn what a first visit could look like.":
    "Contáctanos para conocer cómo podría ser una primera visita.",
  "Request information to get started.": "Solicita información para comenzar.",

  // Testimonials
  "We are so grateful for our son's Occupational Therapist. She is incredibly talented, dedicated, and truly passionate about what she does. She consistently brings creativity and care to every session, and we've seen so much growth in our child because of her.":
    "Estamos muy agradecidos por la Terapeuta Ocupacional de nuestro hijo. Es increíblemente talentosa, dedicada y verdaderamente apasionada por lo que hace. Aporta creatividad y cariño en cada sesión, y hemos visto mucho crecimiento en nuestro hijo gracias a ella.",
  "Seven months ago I arrived at Amenity and I can say that I found two wonderful therapists. I love that there is communication with them; they make me feel welcome when I arrive with my daughters. Thank you, for all your support. Thank you so much for accommodating my schedule when I haven't been able to come. I am very grateful for your work.":
    "Hace siete meses llegué a Amenity y puedo decir que encontré dos terapeutas maravillosos. Me encanta que haya comunicación con ellos; me hacen sentir bienvenida cuando llego con mis hijas. Gracias por todo su apoyo. Muchas gracias por acomodar mi horario cuando no he podido asistir. Estoy muy agradecida por su trabajo.",
  "We have used Amenity for a few years now and have loved the convenience of being able to have therapy in our home. Our therapist is amazing and has helped our daughter achieve so many speech goals. We don't know what we would have done without her and this facility.":
    "Hemos usado Amenity durante algunos años y nos ha encantado la comodidad de poder tener terapia en nuestro hogar. Nuestra terapeuta es increíble y ha ayudado a nuestra hija a lograr muchas metas del habla. No sabemos qué habríamos hecho sin ella y sin este centro.",
  "Amenity family": "Familia de Amenity",
  "3 months ago": "hace 3 meses",
  "2 months ago": "hace 2 meses",
  "a year ago": "hace un año",

  // Positions
  "ABA Therapist / RBT": "Terapeuta ABA / RBT",
  "BCBA (Board Certified Behavior Analyst)": "BCBA (Analista de Conducta Certificado)",
  "Speech-Language Pathologist": "Patólogo/a del habla y lenguaje",
  "Occupational Therapist": "Terapeuta ocupacional",
  "Physical Therapist": "Terapeuta físico/a",
  "Administrative / Front Desk": "Administrativo/a / Recepción",
  "Other": "Otro",

  // Contact page
  "Call us": "Llámanos",
  "Visit": "Visita",
  "Follow us": "Síguenos",
  "Hours": "Horario",
  "Request information": "Solicitar información",

  // Contact form
  "Your name": "Tu nombre",
  "Share a little about what you're looking for. Please do not include private medical information.":
    "Cuéntanos un poco sobre lo que buscas. Por favor, no incluyas información médica privada.",
  "(optional)": "(opcional)",
  "Images or documents, up to 5 files, 10MB each.":
    "Imágenes o documentos, hasta 5 archivos, 10 MB cada uno.",
  "For your privacy, please do not submit private medical information through this form. We'll follow up to gather any details securely.":
    "Para tu privacidad, por favor no envíes información médica privada a través de este formulario. Nos comunicaremos contigo para recopilar cualquier detalle de forma segura.",
  "Remove": "Eliminar",
  "You can attach up to 5 files.": "Puedes adjuntar hasta 5 archivos.",
  "is larger than 10MB.": "supera los 10 MB.",
  "unsupported type or too large.": "tipo no admitido o demasiado grande.",
  "Could not upload": "No se pudo subir",
  "Please add your name and a short message.": "Por favor agrega tu nombre y un breve mensaje.",
  "Please provide either an email or a phone number.": "Por favor proporciona un correo electrónico o un número de teléfono.",
  "Thank you — your request has been received. We'll be in touch soon.":
    "Gracias — hemos recibido tu solicitud. Nos pondremos en contacto pronto.",
  "Something went wrong. Please try again or call us directly.":
    "Algo salió mal. Por favor intenta de nuevo o llámanos directamente.",

  // Emergency notice
  "For emergencies, call 911": "Para emergencias, llame al 911",
  "or go to the nearest emergency room. This website is not for urgent or medical emergencies.":
    "o acuda a la sala de emergencias más cercana. Este sitio web no es para urgencias ni emergencias médicas.",

  // Service Areas page
  "Areas we serve": "Áreas que servimos",
  "Outside our service area?": "¿Fuera de nuestra área de servicio?",
  "Give us a call — we're always happy to talk through your situation and, when we can, help point you toward the right resources.":
    "Llámanos: siempre estamos felices de conversar sobre tu situación y, cuando podamos, ayudarte a encontrar los recursos adecuados.",
  "Not sure if we serve your area? Let's find out together.":
    "¿No estás seguro/a si servimos tu área? Descubrámoslo juntos.",

  // Schedule Tour page
  "Tour hours": "Horario de visitas",
  "Our locations": "Nuestras ubicaciones",
  "Which location?": "¿Qué ubicación?",
  "Preferred date": "Fecha preferida",
  "Weekdays only (Mon–Fri).": "Solo días entre semana (Lun–Vie).",
  "Preferred time": "Hora preferida",
  "Who's visiting?": "¿Quién visita?",
  "e.g., myself and my child": "p. ej., yo y mi hijo/a",
  "Anything we should know?": "¿Algo que debamos saber?",
  "Share any questions. Please don't include private medical information.":
    "Comparte cualquier pregunta. Por favor no incluyas información médica privada.",
  "Please add your name.": "Por favor agrega tu nombre.",
  "Please provide an email or a phone number.": "Por favor proporciona un correo electrónico o un número de teléfono.",
  "Please choose a preferred date.": "Por favor elige una fecha preferida.",
  "Tours are available Monday–Friday. Please pick a weekday.":
    "Las visitas están disponibles de lunes a viernes. Por favor elige un día entre semana.",
  "Tour request received! We'll confirm your visit soon.":
    "¡Solicitud de visita recibida! Confirmaremos tu visita pronto.",
  "Something went wrong. Please try again or call us.":
    "Algo salió mal. Por favor intenta de nuevo o llámanos.",

  // Join Team page
  "Why work with us": "Por qué trabajar con nosotros",
  "A place to do work that matters": "Un lugar para hacer un trabajo que importa",
  "Mission-driven work": "Trabajo con propósito",
  "Make a real difference in the lives of children and families every single day.":
    "Marca una verdadera diferencia en la vida de los niños y las familias cada día.",
  "Supportive team": "Equipo que te apoya",
  "Join a warm, collaborative team that has your back and celebrates your growth.":
    "Únete a un equipo cálido y colaborativo que te respalda y celebra tu crecimiento.",
  "Learning & growth": "Aprendizaje y crecimiento",
  "Opportunities to develop your skills and advance in a caring, expert environment.":
    "Oportunidades para desarrollar tus habilidades y avanzar en un entorno experto y afectuoso.",
  "Meaningful moments": "Momentos significativos",
  "Be there for the milestones — the first words, the big steps, the proud smiles.":
    "Acompaña los grandes momentos: las primeras palabras, los grandes pasos, las sonrisas de orgullo.",
  "Apply now": "Postúlate ahora",
  "Ready to apply?": "¿Listo/a para postularte?",
  "Choose the team you'd like to join and you'll be taken to our careers portal to view openings and apply.":
    "Elige el equipo al que te gustaría unirte y te llevaremos a nuestro portal de empleo para ver las vacantes y postularte.",
  "Join Amenity Health Services": "Únete a Amenity Health Services",
  "Join Amenity ABA Center": "Únete a Amenity ABA Center",

  // Privacy page
  "Placeholder content — not legal advice. Replace before publishing.":
    "Contenido de ejemplo — no es asesoría legal. Reemplázalo antes de publicar.",
  "Overview": "Resumen",
  "This Privacy Policy is a placeholder. Replace it with your organization's finalized policy, reviewed by qualified legal counsel, before launch. It should describe what information you collect and how it is used and protected.":
    "Esta Política de Privacidad es un ejemplo. Reemplázala con la política final de tu organización, revisada por asesoría legal calificada, antes del lanzamiento. Debe describir qué información recopilas y cómo se usa y protege.",
  "Information we collect": "Información que recopilamos",
  "Describe the information collected through this website — for example, the name, phone, email, preferred contact method, and message submitted through the request form.":
    "Describe la información recopilada a través de este sitio web, por ejemplo, el nombre, el teléfono, el correo electrónico, el método de contacto preferido y el mensaje enviado mediante el formulario de solicitud.",
  "How we use information": "Cómo usamos la información",
  "Explain how submitted information is used (e.g., to respond to inquiries and follow up about services) and confirm that visitors should not submit private medical information through the form.":
    "Explica cómo se usa la información enviada (p. ej., para responder consultas y dar seguimiento sobre los servicios) y confirma que los visitantes no deben enviar información médica privada a través del formulario.",
  "Data sharing & security": "Compartir datos y seguridad",
  "Describe whether and how information is shared and the reasonable measures taken to protect it. Do not imply HIPAA compliance unless the appropriate systems and policies are actually in place.":
    "Describe si se comparte la información y cómo, y las medidas razonables tomadas para protegerla. No sugieras cumplimiento de HIPAA a menos que los sistemas y las políticas apropiadas estén realmente implementados.",

  // Accessibility page
  "Amenity Health Services strives to ensure our website is accessible and ADA-conscious. The following reflects our ongoing commitment (edit as your practices are confirmed):":
    "Amenity Health Services se esfuerza por garantizar que nuestro sitio web sea accesible y consciente de la ADA. Lo siguiente refleja nuestro compromiso continuo (edítalo a medida que se confirmen tus prácticas):",
  "We aim to meet WCAG 2.1 AA guidelines across the site.": "Buscamos cumplir con las pautas WCAG 2.1 AA en todo el sitio.",
  "Color combinations are chosen for readable contrast.": "Las combinaciones de color se eligen para un contraste legible.",
  "The site is keyboard-navigable with visible focus indicators.": "El sitio se puede navegar con el teclado y tiene indicadores de enfoque visibles.",
  "Images include descriptive alternative text.": "Las imágenes incluyen texto alternativo descriptivo.",
  "Form fields have clear, associated labels.": "Los campos del formulario tienen etiquetas claras y asociadas.",
  "Motion respects the 'reduce motion' system preference.": "El movimiento respeta la preferencia del sistema de 'reducir movimiento'.",
  "Need help or found a barrier?": "¿Necesitas ayuda o encontraste una barrera?",
  "If you experience any difficulty using this site, please contact us at": "Si tienes alguna dificultad al usar este sitio, contáctanos en",
  "or": "o",
  "and we'll be glad to assist.": "y con gusto te ayudaremos.",

  // About page
  "Our mission": "Nuestra misión",
  "Helping every child feel supported, understood, and capable.":
    "Ayudar a que cada niño se sienta apoyado, comprendido y capaz.",
  "Our mission is to provide compassionate, family-centered developmental care that helps children build the skills and confidence they need to flourish. We believe progress happens best in an environment of trust, patience, and genuine care — for the child and for the whole family.":
    "Nuestra misión es brindar atención del desarrollo con compasión y centrada en la familia, que ayude a los niños a construir las habilidades y la confianza que necesitan para florecer. Creemos que el progreso ocurre mejor en un entorno de confianza, paciencia y cuidado genuino, para el niño y para toda la familia.",
  "Our values": "Nuestros valores",
  "What makes us S.O.A.R.": "Lo que nos hace S.O.A.R.",
  "S — Sincerity": "S — Sinceridad",
  "O — Ownership": "O — Responsabilidad",
  "A — Advocacy": "A — Defensa",
  "R — Resilience": "R — Resiliencia",
  "We show up honest, warm, and genuine with every child and family we serve.":
    "Nos mostramos honestos, cálidos y genuinos con cada niño y familia que servimos.",
  "We take responsibility for our work and follow through on every commitment.":
    "Asumimos la responsabilidad de nuestro trabajo y cumplimos cada compromiso.",
  "We stand up for each child's needs and help families navigate every step.":
    "Defendemos las necesidades de cada niño y ayudamos a las familias a recorrer cada paso.",
  "We meet challenges with patience, persistence, and unwavering encouragement.":
    "Enfrentamos los retos con paciencia, persistencia y aliento inquebrantable.",
  "Honest, transparent care at every step — from staffing and scheduling to quality of care. We believe sincere honesty is the foundation of trust.":
    "Atención honesta y transparente en cada paso, desde la disponibilidad de personal y los horarios hasta la calidad del cuidado. Creemos que la honestidad sincera es la base de la confianza.",
  "We lead with accountability and initiative in every detail — learning your family's dynamics, routines, and favorite things to build truly personalized care.":
    "Actuamos con responsabilidad e iniciativa en cada detalle, conociendo la dinámica, las rutinas y los gustos de tu familia para brindar un cuidado verdaderamente personalizado.",
  "Every voice matters. We listen closely and stay active with managed care and pediatric organizations so families and staff alike are heard and supported.":
    "Cada voz importa. Escuchamos con atención y mantenemos presencia con las organizaciones de atención administrada y pediátrica para que tanto las familias como el personal sean escuchados y apoyados.",
  "We meet challenges with hard work and tenacity, always finding ways to solve problems and give our clients the support they deserve to soar into a better tomorrow.":
    "Enfrentamos los retos con trabajo duro y tenacidad, buscando siempre resolver problemas y brindar a nuestros clientes el apoyo que merecen para volar hacia un mejor mañana.",
  "Our story": "Nuestra historia",
  "How Amenity Health Services began": "Cómo comenzó Amenity Health Services",
  "Amenity Health Services was founded to bring warm, family-centered developmental care to the children and families of Houston. We saw families searching for support that felt personal, patient, and truly on their side.":
    "Amenity Health Services se fundó para llevar una atención del desarrollo cálida y centrada en la familia a los niños y las familias de Houston. Vimos a familias buscando un apoyo que se sintiera personal, paciente y verdaderamente de su lado.",
  "From our first sessions to today, our approach has stayed the same: listen closely, celebrate every milestone, and walk alongside each family with compassion, respect, and genuine care.":
    "Desde nuestras primeras sesiones hasta hoy, nuestro enfoque ha sido el mismo: escuchar con atención, celebrar cada logro y acompañar a cada familia con compasión, respeto y cuidado genuino.",
  "Our commitment: compassionate, respectful care for every child and family.":
    "Nuestro compromiso: atención compasiva y respetuosa para cada niño y familia.",
  "We promise to listen, to explain things clearly, and to honor the trust you place in us. Every family deserves to feel welcomed, heard, and supported — and that's exactly what we aim to provide.":
    "Prometemos escuchar, explicar las cosas con claridad y honrar la confianza que depositas en nosotros. Cada familia merece sentirse bienvenida, escuchada y apoyada, y eso es exactamente lo que buscamos brindar.",

  // Footer
  "Let's ": "",
  "Reach out any time — we're here to help your family find the right next step.":
    "Contáctanos cuando quieras: estamos aquí para ayudar a tu familia a encontrar el siguiente paso adecuado.",

  // Growth journey
  "Step": "Paso",
  "View our locations": "Ver nuestras ubicaciones",
  "Family-first care": "Cuidado que pone a la familia primero",
  "Gentle, playful, patient": "Suave, lúdico, paciente",
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (s) => s });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("amenity_lang") || "en");
  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("amenity_lang", l);
  }, []);
  const t = useCallback(
    (s) => (lang === "es" && Object.prototype.hasOwnProperty.call(ES, s) ? ES[s] : s),
    [lang]
  );
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
